// Global Provider Skill Assignments — admin-only UI
// Sets the default skill for ALL orgs per provider
// Orgs without their own assignment inherit the global one

import { BookOpen, Brain, Globe, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import {
  AiSkill,
  getGlobalSkills,
  getGlobalSkillAssignments,
  SkillAssignment,
  upsertGlobalSkillAssignment,
} from '../../services/aiSkillsService';

type ProviderConfig = {
  id: 'knowledge-base' | 'certant-ai';
  name: string;
  description: string;
  icon: typeof BookOpen;
};

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'knowledge-base',
    name: 'Knowledge Base',
    description: 'RAG with uploaded documents — applies to all orgs by default',
    icon: BookOpen,
  },
  {
    id: 'certant-ai',
    name: 'Certant AI',
    description: 'GraphRAG — applies to all orgs by default',
    icon: Brain,
  },
];

export const GlobalProviderAssignments = () => {
  const { toast } = useToast();
  const [skills, setSkills] = useState<AiSkill[]>([]);
  const [assignments, setAssignments] = useState<SkillAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // Track local selection state per provider
  const [selections, setSelections] = useState<Record<string, string>>({
    'knowledge-base': '',
    'certant-ai': '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [skillsData, assignmentsData] = await Promise.all([
          getGlobalSkills(),
          getGlobalSkillAssignments(),
        ]);
        setSkills(skillsData);
        setAssignments(assignmentsData);

        // Initialize selections from current global assignments
        const initial: Record<string, string> = { 'knowledge-base': '', 'certant-ai': '' };
        for (const a of assignmentsData) {
          initial[a.provider] = a.skill_id ?? '';
        }
        setSelections(initial);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load data';
        toast({ title: 'Error', description: message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (providerId: 'knowledge-base' | 'certant-ai') => {
    setSaving(providerId);
    try {
      const skillId = selections[providerId] || null;
      await upsertGlobalSkillAssignment(providerId, skillId);

      // Refresh assignments
      const updated = await getGlobalSkillAssignments();
      setAssignments(updated);

      const skill = skills.find((s) => s.id === skillId);
      toast({
        title: 'Global assignment saved',
        description: skill
          ? `"${skill.skill_name}" set as global default for ${PROVIDERS.find((p) => p.id === providerId)?.name}`
          : `Global default removed from ${PROVIDERS.find((p) => p.id === providerId)?.name}`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save global assignment';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading global assignments...</span>
      </div>
    );
  }

  const currentAssignment = (providerId: string) =>
    assignments.find((a) => a.provider === providerId);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold tracking-tight flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Global Default Assignments
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Set the default skill for each provider. All organisations inherit this unless they set their own assignment.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PROVIDERS.map((prov) => {
          const Icon = prov.icon;
          const current = currentAssignment(prov.id);
          const selectedId = selections[prov.id];
          const hasChanged = (current?.skill_id ?? '') !== selectedId;

          return (
            <div
              key={prov.id}
              className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3 bg-blue-50/30 dark:bg-blue-950/20"
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{prov.name}</p>
                  <p className="text-xs text-muted-foreground">{prov.description}</p>
                </div>
              </div>

              {current?.skill && (
                <div className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 inline-block">
                  Active: {current.skill.skill_name}
                </div>
              )}

              <div className="flex items-center gap-2">
                <select
                  value={selectedId}
                  onChange={(e) =>
                    setSelections((prev) => ({ ...prev, [prov.id]: e.target.value }))
                  }
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">None - no global default</option>
                  {skills.length === 0 && (
                    <option disabled>No global skills - create one first</option>
                  )}
                  {skills.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.skill_name}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => handleSave(prov.id)}
                  disabled={!hasChanged || saving === prov.id}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving === prov.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
